/**
 * Honest language mapping for the editor kernel.
 *
 * A file gets a highlighter only when this package actually ships that
 * language's parser. A related language is not a substitute: Ruby is not
 * Python, Kotlin is not Java, C# / Swift / Objective-C are not C++,
 * TOML / ini / env are not YAML, and MDX is not Markdown (JSX).
 * Unsupported paths return no extension and `languageId: null`, so a
 * host can say "plain text" rather than pretend the colours mean something.
 */
import { cpp } from "@codemirror/lang-cpp";
import { css } from "@codemirror/lang-css";
import { go } from "@codemirror/lang-go";
import { html } from "@codemirror/lang-html";
import { java } from "@codemirror/lang-java";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { php } from "@codemirror/lang-php";
import { python } from "@codemirror/lang-python";
import { rust } from "@codemirror/lang-rust";
import { sql } from "@codemirror/lang-sql";
import { xml } from "@codemirror/lang-xml";
import { yaml } from "@codemirror/lang-yaml";
import type { Extension } from "@codemirror/state";

/** Parser id when a dedicated CodeMirror language package matched; else null. */
export type EditorLanguageId =
  | "javascript"
  | "typescript"
  | "json"
  | "markdown"
  | "css"
  | "html"
  | "xml"
  | "python"
  | "go"
  | "rust"
  | "java"
  | "cpp"
  | "php"
  | "yaml"
  | "sql";

export interface LanguageMapping {
  languageId: EditorLanguageId | null;
  extension: Extension;
}

const JS = new Set(["js", "jsx", "mjs", "cjs"]);
const TS = new Set(["ts", "tsx"]);
const MD = new Set(["md", "markdown"]);
const HTML = new Set(["html", "htm"]);
const XML = new Set(["xml", "svg", "plist"]);
const CPP = new Set(["c", "h", "cpp", "cc", "cxx", "hpp", "hxx"]);
const YAML = new Set(["yml", "yaml"]);

function extOf(path: string): string {
  const base = path.split(/[/\\]/).pop() ?? path;
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return "";
  return base.slice(dot + 1).toLowerCase();
}

/**
 * Map a document path to the parser that actually understands it.
 * Never returns a stand-in language for an unsupported extension.
 */
export function languageFor(path: string): LanguageMapping {
  const ext = extOf(path);
  if (JS.has(ext)) {
    return {
      languageId: "javascript",
      extension: javascript({ jsx: ext === "jsx" }),
    };
  }
  if (TS.has(ext)) {
    return {
      languageId: "typescript",
      extension: javascript({ typescript: true, jsx: ext === "tsx" }),
    };
  }
  if (ext === "json") return { languageId: "json", extension: json() };
  if (MD.has(ext)) return { languageId: "markdown", extension: markdown() };
  if (ext === "css") return { languageId: "css", extension: css() };
  if (HTML.has(ext)) return { languageId: "html", extension: html() };
  if (XML.has(ext)) return { languageId: "xml", extension: xml() };
  if (ext === "py") return { languageId: "python", extension: python() };
  if (ext === "go") return { languageId: "go", extension: go() };
  if (ext === "rs") return { languageId: "rust", extension: rust() };
  if (ext === "java") return { languageId: "java", extension: java() };
  if (CPP.has(ext)) return { languageId: "cpp", extension: cpp() };
  if (ext === "php") return { languageId: "php", extension: php() };
  if (YAML.has(ext)) return { languageId: "yaml", extension: yaml() };
  if (ext === "sql") return { languageId: "sql", extension: sql() };
  return { languageId: null, extension: [] };
}
